<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Copy16, Edit16, TrashCan16 } from '@carbon/icons-vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['toggle', 'edit', 'delete', 'clone']);

const { t } = useI18n();

const readableDate = date => messageStamp(new Date(date), 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date), 'LLL d, yyyy hh:mm a');

const automationActive = computed({
  get: () => props.automation.active,
  set: active => {
    const { id, name } = props.automation;
    emit('toggle', {
      id,
      name,
      status: !active,
    });
  },
});

// R2-9-4: the kit's ⋮ menu (Edit, Clone, Delete) in place of the three icon buttons.
const menuItems = computed(() => [
  { label: t('SETTINGS_TABLE.EDIT'), value: 'edit', icon: Edit16 },
  { label: t('AUTOMATION.CLONE.TOOLTIP'), value: 'clone', icon: Copy16 },
  {
    label: t('SETTINGS_TABLE.DELETE'),
    value: 'delete',
    icon: TrashCan16,
    destructive: true,
  },
]);

const onAction = action => {
  if (action === 'edit') emit('edit', props.automation);
  else if (action === 'clone') emit('clone', props.automation);
  else emit('delete', props.automation);
};
</script>

<template>
  <BaseTableRow :item="automation">
    <BaseTableCell wrap>
      <span class="block">{{ automation.name }}</span>
      <span
        v-if="automation.description"
        class="mt-0.5 block font-normal text-polaris-muted-foreground"
      >
        {{ automation.description }}
      </span>
    </BaseTableCell>
    <BaseTableCell>
      <ToggleSwitch
        v-model="automationActive"
        :aria-label="t('AUTOMATION.LIST.TABLE_HEADER.ACTIVE')"
      />
    </BaseTableCell>
    <BaseTableCell :title="readableDateWithTime(automation.created_on)">
      {{ readableDate(automation.created_on) }}
    </BaseTableCell>
    <BaseTableCell actions>
      <RowMenu
        :label="t('SETTINGS_TABLE.ROW_ACTIONS', { name: automation.name })"
        :items="menuItems"
        @action="onAction"
      />
    </BaseTableCell>
  </BaseTableRow>
</template>
